import styled from "@emotion/styled"
import { PInlineNotification } from "@porsche-design-system/components-react"
import type { InlineNotificationState } from "@porsche-design-system/components-react"
import {
	breakpoint,
	spacingFluidMd,
	spacingFluidXl,
	spacingStaticMd,
} from "@porsche-design-system/components-react/emotion"
import type { WarrantyPublicOfferView } from "@slfinrtl/ui-bff-routes"
import type { JSX } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { porscheApprovedMessages } from "src/common/messages/porscheApproved.messages"
import { Grid, GridItem } from "src/components"
import { PorscheApprovedDecisionAndVehicleWarranties } from "../PorscheApprovedDecisionAndVehicleWarranties"
import { isDealerDecisionValid } from "../isDealerDecisionValid"
import { useDisablementReasons } from "../useDisablementReasons"

export const WarrantyDataView = ({
	editMode,
	errorMessage,
	onDealerDecisionChanged,
	view,
}: {
	editMode: boolean
	errorMessage?: string
	onDealerDecisionChanged: (checked: boolean) => void
	view: WarrantyPublicOfferView
}) => {
	const { formatMessage } = useIntl()

	const disablementReasons = useDisablementReasons(view)
	const { canBePorscheApproved, dealerDecisionPorscheApproved, durationToBeReported, reportedWarranties } = view

	let warrantyStatusText: JSX.Element | undefined
	if (canBePorscheApproved.ok && dealerDecisionPorscheApproved) {
		if (durationToBeReported) {
			warrantyStatusText = (
				<FormattedMessage
					{...porscheApprovedMessages.warrantyWillBeReported}
					values={{
						warrantyPeriod: durationToBeReported,
					}}
				/>
			)
		} else {
			warrantyStatusText = <FormattedMessage {...porscheApprovedMessages.warrantyReportingNotNecessary} />
		}
	}

	let notification: { message: string; state: InlineNotificationState } | undefined

	if (errorMessage) {
		notification = {
			message: errorMessage,
			state: "error",
		}
	} else if (!isDealerDecisionValid(view)) {
		notification = {
			message: formatMessage(porscheApprovedMessages.formerSelectionInvalid),
			state: "warning",
		}
	}

	return (
		<>
			<PorscheApprovedDecisionAndVehicleWarranties
				editMode={editMode}
				onDealerDecisionChanged={onDealerDecisionChanged}
				dealerDecisionPorscheApproved={dealerDecisionPorscheApproved}
				reportedWarranties={reportedWarranties}
				disablementReasons={disablementReasons}
			/>

			{warrantyStatusText && (
				<Grid $columnGap={{ base: spacingFluidMd, s: spacingFluidXl }}>
					<GridItem size={{ base: 12, l: 6 }}>
						<SpacingStaticMediumMobile />
						<PInlineNotification
							state={durationToBeReported ? "info" : "success"}
							dismissButton={false}
							data-testid="warranty-data-status-info"
						>
							{warrantyStatusText}
						</PInlineNotification>
					</GridItem>
				</Grid>
			)}

			{notification && (
				<PInlineNotification state={notification.state} dismissButton={false}>
					{notification.message}
				</PInlineNotification>
			)}
		</>
	)
}

const SpacingStaticMediumMobile = styled.div`
	@media (max-width: ${breakpoint.l - 1}px) {
		margin-bottom: ${spacingStaticMd};
	}
`
